import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Check, ChevronDown, ChevronUp, Upload, X, Plus } from "lucide-react";
import * as Select from "@radix-ui/react-select";
import * as Checkbox from "@radix-ui/react-checkbox";
import * as RadioGroup from "@radix-ui/react-radio-group";
import * as Toggle from "@radix-ui/react-toggle";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useDropzone } from "react-dropzone";
import { AspectRatio } from "@radix-ui/react-aspect-ratio";
import * as Dialog from "@radix-ui/react-dialog";
import { Command } from "cmdk";

// Helper: normalize options to { value, label }
const normalizeOptions = (options = []) =>
  options.map((opt) =>
    typeof opt === "string" ? { value: opt, label: opt } : opt
  );

// ---- Field shell ----
export const Field = ({ label, required, error, hint, children }) => (
  <div>
    {label && (
      <label className="block text-sm font-medium text-text-primary mb-1">
        {label} {required && <span className="text-error">*</span>}
      </label>
    )}
    {children}
    {error ? (
      <p className="text-error text-sm mt-1">{error}</p>
    ) : hint ? (
      <p className="text-text-tertiary text-xs mt-1">{hint}</p>
    ) : null}
  </div>
);

// ---- Text input ----
export const TextField = ({
  type = "text",
  label,
  error,
  hint,
  required,
  className = "",
  numbersOnly = false, // Add this flag
  ...props
}) => {
  // Add handler for keypress
  const handleKeyPress = (event) => {
    if (numbersOnly) {
      // Allow only numbers and control keys (backspace, delete, arrows etc)
      if (!/[0-9]/.test(event.key) && event.key.length === 1) {
        event.preventDefault();
      }
    }
  };

  return (
    <Field label={label} error={error} hint={hint} required={required}>
      <input
        type={type}
        inputMode={numbersOnly ? "numeric" : "text"}
        pattern={numbersOnly ? "[0-9]*" : undefined}
        onKeyPress={handleKeyPress}
        className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary transition-colors ${
          error ? "border-error" : "border-border-medium"
        } bg-bg-primary text-text-primary ${className}`}
        {...props}
      />
    </Field>
  );
};

// ---- Textarea ----
export const TextAreaField = ({
  label,
  error,
  hint,
  required,
  className = "",
  rows = 3,
  ...props
}) => (
  <Field label={label} error={error} hint={hint} required={required}>
    <textarea
      rows={rows}
      className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary transition-colors ${
        error ? "border-error" : "border-border-medium"
      } bg-bg-primary text-text-primary ${className}`}
      {...props}
    />
  </Field>
);

// ---- File picker ----
export const FileField = ({
  label,
  error,
  hint,
  required,
  className = "",
  multiple = false,
  accept,
  onChange,
  value,
}) => {
  const fileInputRef = React.useRef(null);

  // Handle file selection
  const handleFileChange = (e) => {
    const files = e.target.files;
    if (multiple) {
      onChange?.(files);
    } else {
      onChange?.(files?.[0] || null);
    }
  };

  // Show selected files info
  const renderFileInfo = () => {
    if (!value) return null;

    const clearFiles = () => {
      onChange?.(multiple ? null : null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    if (multiple && value instanceof FileList) {
      const fileCount = value.length;
      return (
        <div className="mt-2 space-y-1">
          <div className="text-sm text-text-tertiary">
            {fileCount} file{fileCount !== 1 ? "s" : ""} selected
          </div>
          {Array.from(value).map((file, index) => (
            <div
              key={index}
              className="text-sm text-text-secondary bg-surface-50 px-2 py-1 rounded flex items-center justify-between"
            >
              <span>
                {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </span>
            </div>
          ))}
          <button
            type="button"
            onClick={clearFiles}
            className="text-sm text-error hover:text-error-600 underline"
          >
            Clear Files
          </button>
        </div>
      );
    }

    if (!multiple && value instanceof File) {
      return (
        <div className="mt-2 space-y-1">
          <div className="text-sm text-text-tertiary">1 file selected</div>
          <div className="text-sm text-text-secondary bg-surface-50 px-2 py-1 rounded flex items-center justify-between">
            <span>
              {value.name} ({(value.size / 1024).toFixed(1)} KB)
            </span>
          </div>
          <button
            type="button"
            onClick={clearFiles}
            className="text-sm text-error hover:text-error-600 underline"
          >
            Clear File
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <Field label={label} error={error} hint={hint} required={required}>
      <input
        ref={fileInputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        onChange={handleFileChange}
        className={`w-full border rounded-md px-3 py-2 focus:outline-none transition-colors ${
          error ? "border-error" : "border-border-medium"
        } bg-bg-primary text-text-primary ${className}`}
      />
      {renderFileInfo()}
    </Field>
  );
};

// ---- Checkbox (boolean) ----
export const CheckboxField = ({
  label,
  checked,
  onChange,
  disabled,
  className = "",
}) => (
  <label
    className={`inline-flex items-center space-x-2 cursor-pointer ${
      disabled ? "opacity-50 cursor-not-allowed" : ""
    } ${className}`}
  >
    <Checkbox.Root
      checked={Boolean(checked)}
      onCheckedChange={(val) => {
        onChange?.(val === true);
      }}
      disabled={disabled}
      className="h-5 w-5 rounded border border-gray-400 
                 flex items-center justify-center 
                 data-[state=checked]:bg-blue-600 
                 data-[state=checked]:border-blue-600"
    >
      {checked && (
        <Checkbox.Indicator className="flex items-center justify-center">
          <Check className="h-4 w-4 text-white" />
        </Checkbox.Indicator>
      )}
    </Checkbox.Root>
    {label && (
      <span className="text-sm text-text-primary select-none">{label}</span>
    )}
  </label>
);

// ---- Checkbox group (multi-select) ----

export const CheckboxGroupField = ({
  label,
  value = [],
  onChange,
  options = [],
  error,
  hint,
  required,
}) => {
  const opts = normalizeOptions(options);
  const current = Array.isArray(value) ? value : [];

  return (
    <Field label={label} error={error} hint={hint} required={required}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {opts.map((opt) => {
          const checked = current.includes(opt.value);
          return (
            <label
              key={opt.value}
              className="inline-flex items-center space-x-2 cursor-pointer"
            >
              <Checkbox.Root
                checked={checked}
                onCheckedChange={(isChecked) => {
                  if (isChecked) {
                    onChange?.([...current, opt.value]); // add
                  } else {
                    onChange?.(current.filter((v) => v !== opt.value)); // remove
                  }
                }}
                className="h-5 w-5 rounded border border-gray-400 
                 flex items-center justify-center 
                 data-[state=checked]:bg-blue-600 
                 data-[state=checked]:border-blue-600"
              >
                {checked && (
                  <Checkbox.Indicator className="flex items-center justify-center">
                    <Check className="h-4 w-4 text-white" />
                  </Checkbox.Indicator>
                )}
              </Checkbox.Root>
              <span className="text-sm text-text-primary">{opt.label}</span>
            </label>
          );
        })}
      </div>
    </Field>
  );
};

// ---- Radio group ----
export const RadioGroupField = ({
  label,
  value,
  onChange,
  options = [],
  error,
  hint,
  required,
  className = "",
}) => {
  const opts = normalizeOptions(options);
  return (
    <Field label={label} error={error} hint={hint} required={required}>
      <RadioGroup.Root
        value={value}
        onValueChange={(val) => onChange?.(val)}
        className={`grid grid-cols-1 sm:grid-cols-2 gap-2 ${className}`}
      >
        {opts.map((opt) => (
          <label
            key={opt.value}
            className="inline-flex items-center space-x-2 cursor-pointer"
          >
            <RadioGroup.Item
              value={opt.value}
              className="h-4 w-4 rounded-full border border-border-medium data-[state=checked]:border-primary data-[state=checked]:ring-4 data-[state=checked]:ring-primary-100 transition-colors"
            />
            <span className="text-sm text-text-primary">{opt.label}</span>
          </label>
        ))}
      </RadioGroup.Root>
    </Field>
  );
};

// ---- Toggle (boolean) using Radix Toggle ----
export const ToggleField = ({
  label,
  pressed,
  onPressedChange,
  className = "",
}) => (
  <div className={`inline-flex items-center space-x-3 ${className}`}>
    {label && <span className="text-sm text-text-primary">{label}</span>}
    <Toggle.Root
      pressed={Boolean(pressed)}
      onPressedChange={(p) => onPressedChange?.(Boolean(p))}
      className={`inline-flex items-center justify-center rounded-md border px-3 py-1.5 text-sm transition-colors ${
        pressed
          ? "bg-primary border-primary text-white"
          : "bg-bg-primary border-border-medium text-text-primary hover:bg-surface-50"
      }`}
      aria-label={label || "Toggle"}
    >
      {pressed ? "On" : "Off"}
    </Toggle.Root>
  </div>
);

export const SelectField = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Select an option",
  error,
  hint,
  required,
  disabled = false,
}) => {
  const opts = normalizeOptions(options);
  return (
    <Field label={label} error={error} hint={hint} required={required}>
      <Select.Root value={value} onValueChange={(val) => onChange?.(val)} disabled={disabled}>
        <Select.Trigger
          className={`w-full inline-flex items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors ${
            error ? "border-red-500" : "border-gray-300 "
          } ${disabled ? "opacity-50 cursor-not-allowed bg-gray-50" : "bg-white"} text-gray-700 `}
          aria-label={placeholder}
        >
          <Select.Value placeholder={placeholder} />
          <Select.Icon>
            <ChevronDown className="h-4 w-4 text-gray-500 " />
          </Select.Icon>
        </Select.Trigger>
        <Select.Portal container={document.body}>
          <Select.Content className="overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg z-[99999]">
            <Select.ScrollUpButton className="flex items-center justify-center py-1 text-gray-500 ">
              <ChevronUp className="h-4 w-4" />
            </Select.ScrollUpButton>
            <Select.Viewport className="p-1">
              {opts.map((opt) => (
                <Select.Item
                  key={opt.value}
                  value={opt.value}
                  className="relative flex cursor-pointer select-none items-center rounded px-2 py-2 text-sm text-gray-700  outline-none data-[highlighted]:bg-blue-50  transition-colors"
                >
                  <Select.ItemText>{opt.label}</Select.ItemText>
                  <Select.ItemIndicator className="absolute right-2 inline-flex items-center">
                    <Check className="h-4 w-4 text-blue-600 " />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.Viewport>
            <Select.ScrollDownButton className="flex items-center justify-center py-1 text-gray-500 ">
              <ChevronDown className="h-4 w-4" />
            </Select.ScrollDownButton>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </Field>
  );
};

// ---- Multi Select (Dropdown + Checkbox items) ----
export const MultiSelectField = ({
  label,
  value = [],
  onChange,
  options = [],
  placeholder = "Select options",
  error,
  hint,
  required,
  disabled = false,
}) => {
  const opts = normalizeOptions(options);
  const current = Array.isArray(value) ? value : [];
  const triggerRef = useRef(null);
  const [menuWidth, setMenuWidth] = useState(undefined);
  const [open, setOpen] = useState(false);

  // Match width of trigger
  useLayoutEffect(() => {
    if (triggerRef.current) {
      setMenuWidth(triggerRef.current.offsetWidth);
    }
  }, [triggerRef.current, open]);

  const toggle = (val, checked) => {
    const next = checked ? [...current, val] : current.filter((v) => v !== val);
    onChange?.(next);
  };

  const selectedLabels = opts
    .filter((o) => current.includes(o.value))
    .map((o) => o.label);

  return (
    <Field label={label} error={error} hint={hint} required={required}>
      <DropdownMenu.Root open={open} onOpenChange={setOpen}>
        <DropdownMenu.Trigger asChild>
          <button
            ref={triggerRef}
            type="button"
            disabled={disabled}
            className={`w-full inline-flex items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors ${
              error ? "border-red-500" : "border-gray-300"
            } ${disabled ? "opacity-50 cursor-not-allowed bg-gray-50" : "bg-white"} text-gray-700`}
          >
            <span className="truncate">
              {selectedLabels.length > 0
                ? selectedLabels.join(", ")
                : placeholder}
            </span>
            <ChevronDown className="h-4 w-4 text-gray-500" />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal container={document.body}>
          <DropdownMenu.Content
            side="bottom"
            align="start"
            avoidCollisions={false}
            className="rounded-md border border-gray-200 bg-white p-1 shadow-md z-[99999] max-h-[300px] overflow-y-auto"
            sideOffset={6}
            style={{ width: menuWidth }}
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            {opts.map((opt) => (
              <DropdownMenu.CheckboxItem
                key={opt.value}
                checked={current.includes(opt.value)}
                onCheckedChange={(checked) => toggle(opt.value, checked)}
                onSelect={(event) => event.preventDefault()}
                className="relative flex cursor-pointer select-none items-center rounded px-2 py-2 text-sm text-gray-700 outline-none data-[highlighted]:bg-blue-50 transition-colors"
              >
                <DropdownMenu.ItemIndicator>
                  <Check className="h-4 w-4 text-blue-600 mr-2" />
                </DropdownMenu.ItemIndicator>
                <span>{opt.label}</span>
              </DropdownMenu.CheckboxItem>
            ))}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </Field>
  );
};
export const TagsField = ({
  label,
  value = [],
  onChange,
  placeholder = "Type and press Enter to add tags",
  error,
  hint,
  required,
}) => {
  const [inputValue, setInputValue] = useState("");

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (inputValue.trim()) {
        const newTag = inputValue.trim();
        if (!value.includes(newTag)) {
          onChange?.([...value, newTag]);
        }
        setInputValue("");
      }
    } else if (e.key === "Backspace" && !inputValue && value.length > 0) {
      // Remove last tag when backspace is pressed on empty input
      onChange?.(value.slice(0, -1));
    }
  };

  const removeTag = (tagToRemove) => {
    onChange?.(value.filter((tag) => tag !== tagToRemove));
  };

  return (
    <Field label={label} error={error} hint={hint} required={required}>
      <div
        className={`min-h-[38px] w-full border rounded-md px-2 py-1 focus-within:ring-2 focus-within:ring-primary transition-colors flex flex-wrap gap-2 items-center ${
          error ? "border-error" : "border-border-medium"
        }`}
      >
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 bg-primary-100 text-primary-700 px-2 py-0.5 rounded-md text-sm"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="hover:text-error-600 focus:outline-none"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[120px] outline-none bg-transparent text-sm py-1"
        />
      </div>
    </Field>
  );
};

export const ComboboxField = ({
  label,
  value = [],
  onChange,
  options = [],
  placeholder = "Select or type to add...",
  error,
  hint,
  required,
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const [opts, setOpts] = useState(normalizeOptions(options));
  const current = Array.isArray(value) ? value : [];

  return (
    <Field label={label} error={error} hint={hint} required={required}>
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Trigger asChild>
          <div
            className={`min-h-[38px] w-full border rounded-md px-2 py-1 cursor-pointer
              ${error ? "border-error" : "border-border-medium"}
              hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary
            `}
          >
            {current.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {current.map((item) => {
                  const opt = opts.find((o) => o.value === item);
                  const label = opt ? opt.label : item;
                  return (
                    <span
                      key={item}
                      className="bg-secondary-dark focus:outline-none inline-flex items-center gap-1  text-white p-2 rounded text-sm"
                    >
                      {label}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onChange?.(current.filter((i) => i !== item));
                        }}
                        className=""
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </span>
                  );
                })}
              </div>
            ) : (
              <span className="text-text-tertiary text-sm">{placeholder}</span>
            )}
          </div>
        </Dialog.Trigger>

        <Dialog.Portal container={document.body}>
          <Dialog.Overlay className="fixed inset-0 bg-black/50" />
          <Dialog.Content className="fixed top-[50%] left-[50%] max-h-[85vh] w-[90vw] max-w-[450px] translate-x-[-50%] translate-y-[-50%] rounded-md bg-white p-4 shadow-lg z-[99999]">
            <Dialog.Title className="text-lg font-medium mb-2">
              Select Services
            </Dialog.Title>
            <Command className="w-full">
              <div className="flex items-center border-b pb-4 mb-4">
                <Command.Input
                  value={inputValue}
                  onValueChange={setInputValue}
                  placeholder="Search or add custom value..."
                  className="w-full outline-none text-sm"
                />
              </div>

              <Command.List className="max-h-[300px] overflow-auto">
                {opts.map((opt) => (
                  <Command.Item
                    key={opt.value}
                    value={opt.value} // ✅ must be a string
                    onSelect={(selected) => {
                      const exists = current.includes(selected);
                      const next = exists
                        ? current.filter((v) => v !== selected)
                        : [...current, selected];
                      onChange?.(next);
                    }}
                    className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded cursor-pointer
                      ${
                        current.includes(opt.value)
                          ? "bg-primary-100 text-primary"
                          : "hover:bg-surface-100"
                      }
                    `}
                  >
                    <div className="w-4 h-4 border rounded flex items-center justify-center">
                      {current.includes(opt.value) && (
                        <Check className="h-3 w-3" />
                      )}
                    </div>
                    {opt.label}
                  </Command.Item>
                ))}

                {inputValue.trim() &&
                  !opts.find((o) => o.value === inputValue.trim()) && (
                    <Command.Item
                      value={inputValue.trim()}
                      onSelect={(selected) => {
                        if (!current.includes(selected)) {
                          onChange?.([...current, selected]);
                          setOpts([
                            ...opts,
                            { value: selected, label: selected },
                          ]);
                        }
                        setInputValue("");
                      }}
                      className="flex items-center gap-2 px-2 py-1.5 text-sm rounded cursor-pointer hover:bg-surface-100"
                    >
                      <Plus className="h-4 w-4" />
                      Add "{inputValue.trim()}"
                    </Command.Item>
                  )}
              </Command.List>
            </Command>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </Field>
  );
};
export default {
  Field,
  TextField,
  TextAreaField,
  FileField,
  CheckboxField,
  CheckboxGroupField,
  RadioGroupField,
  ToggleField,
  SelectField,
  MultiSelectField,
  TagsField,
  ComboboxField,
};
